import React, { useState } from "react";
import { motion } from "framer-motion";
import { Facebook, Linkedin, Sparkles, Copy, Check, Wand2 } from "lucide-react";
import { useLang } from "@/i18n";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const MarketingAI = ({ embedded = false }) => {
  const { t, lang } = useLang();
  const m = t.marketing;
  const [platform, setPlatform] = useState("linkedin");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professionnel");
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [copied, setCopied] = useState(-1);

  const generate = async () => {
    if (!topic.trim()) return;
    setLoading(true); setPosts([]);
    try {
      const { data } = await api.post("/marketing/generate", { platform, topic, tone, lang });
      setPosts(data.posts || []);
    } catch (e) {
      toast.error("Erreur de génération, réessayez.");
    } finally { setLoading(false); }
  };

  const copy = async (text, i) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        toast.error("Copie impossible, sélectionnez le texte manuellement.");
        return;
      }
    }
    setCopied(i);
    toast.success(m.copied);
    setTimeout(() => setCopied(-1), 1800);
  };

  const tones = { professionnel: "Pro", inspirant: "Inspirant", commercial: "Commercial", amical: "Amical" };

  return (
    <section id="marketing" className={embedded ? "relative" : "relative py-24 sm:py-32 max-w-6xl mx-auto px-5 sm:px-8"} data-testid="marketing-ai">
      <div className={embedded ? "mb-6" : "mb-12 text-center max-w-2xl mx-auto"}>
        <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 border border-accent/30 px-4 py-1.5 mb-5">
          <Sparkles className="w-4 h-4 text-accent" strokeWidth={1.5} />
          <span className="text-sm text-accent">AI Marketing</span>
        </div>
        <h2 className={embedded ? "font-display text-2xl sm:text-3xl font-bold" : "font-display text-3xl sm:text-5xl font-bold"}>{m.title}</h2>
        <p className="mt-4 text-muted-foreground text-lg">{m.subtitle}</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="lg:col-span-2 glow-border glass rounded-2xl p-6 space-y-5 h-fit" data-testid="marketing-form">
          <div>
            <label className="text-sm text-muted-foreground">{m.platform}</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button onClick={() => setPlatform("linkedin")} data-testid="platform-linkedin"
                className={`flex items-center justify-center gap-2 rounded-xl border py-3 text-sm transition-colors ${platform === "linkedin" ? "border-accent bg-accent/15 text-white" : "border-white/10 text-muted-foreground hover:border-white/20"}`}>
                <Linkedin className="w-4 h-4" strokeWidth={1.5} /> LinkedIn
              </button>
              <button onClick={() => setPlatform("facebook")} data-testid="platform-facebook"
                className={`flex items-center justify-center gap-2 rounded-xl border py-3 text-sm transition-colors ${platform === "facebook" ? "border-accent bg-accent/15 text-white" : "border-white/10 text-muted-foreground hover:border-white/20"}`}>
                <Facebook className="w-4 h-4" strokeWidth={1.5} /> Facebook
              </button>
            </div>
          </div>
          <div>
            <label className="text-sm text-muted-foreground">{m.topic}</label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={m.topicPh} data-testid="marketing-topic"
              className="mt-2 bg-white/5 border-white/10 focus-visible:ring-accent" />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">{m.tone}</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(tones).map(([k, v]) => (
                <button key={k} onClick={() => setTone(k)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tone === k ? "border-accent bg-accent/15" : "border-white/10 text-muted-foreground"}`}>{v}</button>
              ))}
            </div>
          </div>
          <Button onClick={generate} disabled={loading || !topic.trim()} data-testid="marketing-generate"
            className="w-full rounded-full bg-accent hover:bg-accent/90 text-white h-12 font-medium">
            <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> {loading ? m.generating : m.generate}
          </Button>
        </motion.div>

        <div className="lg:col-span-3 space-y-4" data-testid="marketing-results">
          {posts.length === 0 && !loading && (
            <div className="h-full min-h-[240px] flex items-center justify-center rounded-2xl border border-dashed border-white/10 p-8 text-center">
              <p className="text-muted-foreground text-sm max-w-xs">{m.empty}</p>
            </div>
          )}
          {loading && [...Array(3)].map((_, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-card p-5 animate-pulse h-28" />
          ))}
          {posts.map((p, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-white/10 bg-card p-5 hover:border-accent/30 transition-colors" data-testid={`marketing-post-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-accent flex items-center gap-1.5">
                  {platform === "linkedin" ? <Linkedin className="w-3.5 h-3.5" /> : <Facebook className="w-3.5 h-3.5" />} Post {i + 1}
                </span>
                <button onClick={() => copy(p, i)} data-testid={`marketing-copy-${i}`}
                  className="flex items-center gap-1.5 text-xs rounded-full border border-white/10 px-3 py-1.5 hover:border-accent hover:text-accent transition-colors">
                  {copied === i ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied === i ? m.copied : m.copy}
                </button>
              </div>
              <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">{p}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
